import React, { Component } from 'react'
import PropTypes from 'prop-types'

import ReactDOM from 'react-dom'
import styles from './RelatedContent.css'

import Insights from '@healthwise-ui/content/Insights'
import MediaServiceVideo from '@healthwise-ui/content/MediaServiceVideo'
import Heading from '../Heading'
import VideoThumbnail from '@healthwise-ui/core/VideoThumbnail'

import ContentAction from '@healthwise-ui/content/Action'
import ContentArticle from '@healthwise-ui/content/Article'
import ContentStory from '@healthwise-ui/content/Story'

class RelatedContent extends Component {
  constructor(props) {
    super(props)

    this.cleanupPreviousVideo = this.cleanupPreviousVideo.bind(this)
    this.renderCloseButton = this.renderCloseButton.bind(this)
    this.focusCloseButton = this.focusCloseButton.bind(this);

    this.insights = new Insights()
    this.thumbnailComponents = {}
    this.videoComponents = {}
    this.articlePositions = {}
    this.activeItemCloseButton = null;
    this.state = {
      activeItem: null
    }
  }

  componentDidUpdate(prevState) {
    // animate window scroll (from the current window position to destination)
    // - tweaked quite a bit from https://stackoverflow.com/a/8918062
    const animateScroll = (to, duration) => {
      if (duration <= 0) return

      const start = window.scrollY || window.pageYOffset
      const difference = start > to ? start - to : start + to
      let perTick = (difference / duration) * 10
      const toScroll = start > to ? start - perTick : start + perTick

      if (perTick < 10) {
        window.scrollTo(0, to)
        return
      }

      setTimeout(function() {
        window.scrollTo(0, toScroll)
        animateScroll(to, duration - 10)
      }, 10)
    }

    const activeItem = this.state.activeItem

    // scroll to new item (in the case that one article is open, then the other article is opened, so it won't be at the bottom)
    if (activeItem && prevState.activeItem !== activeItem) {
      const headerOffset = 70
      animateScroll(this.articlePositions[activeItem.id] - headerOffset, 500)
      this.focusCloseButton();
    }
  }

  cleanupPreviousVideo(itemToClean) {
    const previousItem = this.state.activeItem

    if (previousItem && previousItem.id && previousItem.type === 'hwVideo') {
      ReactDOM.unmountComponentAtNode(this.videoComponents[previousItem.id])
    }
  }

  onVideoThumbnailClick(item) {
    const { mediaHash, mediaServiceUrl, mediaToken } = this.props
    const animationDelay = 300 // ms

    if (this.state.activeItem !== item) {
      this.cleanupPreviousVideo()

      ReactDOM.render(
        <MediaServiceVideo
          hideDisclaimer
          setFocus
          hideType
          hideAbstract
          updateTimeout={animationDelay}
          mediaHash={mediaHash}
          item={item}
          mediaServiceUrl={mediaServiceUrl}
          mediaToken={mediaToken}
        />,
        this.videoComponents[item.id]
      )

      this.setState({ activeItem: item })
    }
  }

  onArticleThumbnailClick(item) {
    if (this.state.activeItem !== item) {
      this.setState({ activeItem: item })
    }
  }

  focusCloseButton() {
    setTimeout(() => {
      this.activeItemCloseButton.focus();
    }, 300);
  }

  onCloseClick(item) {
    if (item && item.id && item.type === 'hwVideo') {
      ReactDOM.unmountComponentAtNode(this.videoComponents[item.id])

      if (this.thumbnailComponents[item.id]) {
        setTimeout(() => {
          this.thumbnailComponents[item.id].querySelector('button').focus() // Prevent "Freak Out" mode
        }, 100) // Wait for animation to complete
      }
    }

    this.setState({ activeItem: null })
  }

  renderCloseButton(item) {
    return (
      <button
        type="button"
        aria-label="Close"
        className={styles.contentClose}
        onClick={() => this.onCloseClick(item)}
        ref={ el => {
          if (this.state.activeItem && this.state.activeItem.id == item.id) {
            this.activeItemCloseButton = el;
          }
        }}
      >
        <svg
          focusable="false"
          role="presentation"
          width="20px"
          height="20px"
          viewBox="0 0 20 20"
          version="1.1"
        >
          <title>Close</title>
          <g transform="translate(-2.000000, -2.000000)">
            <polygon
              points="22 4.01428571 19.9857143 2 12 9.98571429 4.01428571 2 2 4.01428571 9.98571429 12 2 19.9857143 4.01428571 22 12 14.0142857 19.9857143 22 22 19.9857143 14.0142857 12"
              fill="#9e9e9e"
              fillRule="evenodd"
              stroke="none"
              strokeWidth="1"
            />
          </g>
        </svg>
      </button>
    )
  }

  render() {
    const itemList = this.props.item
    const renderCloseButton = this.renderCloseButton
    const thumbnails = []
    const articlePositions = this.articlePositions

    itemList.topics.forEach((item, i) => {
      if (item.thumbnail) {
        if (i > 0) {
          thumbnails.push(<hr className={styles.rule} key={`hr${i}`} />)
        }
        const isActiveItem = this.state.activeItem === item

        switch (item.type) {
          case 'hwVideo':
            const videoClickAction = isActiveItem
              ? null
              : () => this.onVideoThumbnailClick(item)
            let durationSegments = item.duration.split(':')

            // change duration format from 00:00:00 to 00:00 (when the video isn't hours long)
            if (durationSegments.length === 3 && durationSegments[0] === '00') {
              durationSegments.shift()
            }

            thumbnails.push(
              <article
                id={`id_${item.id}`}
                key={i}
                className={
                  styles.contentArticle + ' ' + styles.contentType_video
                }
                aria-expanded={this.state.activeItem === item}
                onClick={videoClickAction}
                ref={me => {
                  // get cross-browser (IE) compatability
                  const toScroll = window.scrollY || window.pageYOffset
                  // set the initial location of this video on the page (before we expand/contract, etc.)
                  if (!articlePositions[item.id])
                    articlePositions[item.id] = Math.round(
                      toScroll + me.getBoundingClientRect().top
                    )
                }}
              >
                {renderCloseButton(item)}

                <div className={styles.content}>
                  <div className={styles.clearfix}>
                    <div className={styles.contentMedia}>
                      <div
                        className={styles.contentThumbnail}
                        ref={element => {
                          this.thumbnailComponents[item.id] = element
                        }}
                      >
                        <VideoThumbnail
                          src={item.thumbnail}
                          title={item.title.consumer}
                        />
                      </div>
                    </div>
                    <div className={styles.contentDescription}>
                      <header className={styles.contentHeader}>
                        <p className="hw-relatedContent-p">Video</p>
                        <Heading headingLevel={4} styleLevel={3}>
                          {item.title.consumer} ({durationSegments.join(':')})
                        </Heading>
                      </header>
                      <p
                        className={
                          'hw-relatedContent-p ' + styles.contentAbstract
                        }
                      >
                        {item.abstract.consumer}
                      </p>
                    </div>
                    <div className={styles.contentMedia}>
                      <div
                        className={styles.contentFull}
                        ref={element => {
                          this.videoComponents[item.id] = element
                        }}
                      />
                    </div>
                  </div>
                </div>
              </article>
            )
            break

          // currently what we get for "Article"
          case 'Map':
          case 'Product Map':
            const articleClickAction = () => this.onArticleThumbnailClick(item)

            thumbnails.push(
              <article
                id={`id_${item.id}`}
                key={i}
                className={
                  styles.contentArticle + ' ' + styles.contentType_article
                }
                aria-expanded={this.state.activeItem === item}
                onClick={articleClickAction}
                ref={me => {
                  // get cross-browser (IE) compatability
                  const toScroll = window.scrollY || window.pageYOffset
                  // set the initial location of this video on the page (before we expand/contract, etc.)
                  if (!articlePositions[item.id])
                    articlePositions[item.id] = Math.round(
                      toScroll + me.getBoundingClientRect().top
                    )
                }}
              >
                {renderCloseButton(item)}

                <div className={styles.content}>
                  <div className={styles.clearfix}>
                    <div className={styles.contentDescription}>
                      <header className={styles.contentHeader}>
                        <p className="hw-relatedContent-p">Article</p>
                        <Heading headingLevel={4} styleLevel={3}>
                          {item.title.consumer}
                        </Heading>
                      </header>
                      <p
                        className={
                          'hw-relatedContent-p ' + styles.contentAbstract
                        }
                      >
                        {item.abstract.consumer}
                      </p>
                    </div>
                    <div className={styles.contentMedia}>
                      <div className={styles.contentThumbnail}>
                        <button type="button" className={styles.articleButton}>
                          <img src={item.thumbnail} alt={item.title.consumer} />
                        </button>
                      </div>
                    </div>
                    <div className={styles.fullArticle}>
                      <div className={styles.contentFull}>
                        <ContentArticle productMap={item} />
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            )
            break

          // "hwTeaser" content type
          // - this type has a nested topic to reveal after user interaction ("click", in this case)
          case 'hwTeaser':
            const teaserClickAction = () => this.onArticleThumbnailClick(item)
            const toShow = item.topics[0]

            const labels = {
              hwAction: 'Tips',
              hwStory: 'Story',
              default: 'Article'
            }
            const label = labels[toShow.type] || labels['default']

            const component =
              toShow.type === 'hwStory' ? (
                <ContentStory data={toShow} />
              ) : (
                <ContentAction data={toShow} />
              )

            thumbnails.push(
              <article
                id={`id_${item.id}`}
                key={i}
                className={
                  styles.contentArticle + ' ' + styles.contentType_article
                }
                aria-expanded={this.state.activeItem === item}
                onClick={teaserClickAction}
                ref={me => {
                  // get cross-browser (IE) compatability
                  const toScroll = window.scrollY || window.pageYOffset
                  // set the initial location of this video on the page (before we expand/contract, etc.)
                  if (!articlePositions[item.id])
                    articlePositions[item.id] = Math.round(
                      toScroll + me.getBoundingClientRect().top
                    )
                }}
              >
                {renderCloseButton(item)}

                <div className={styles.content}>
                  <div className={styles.clearfix}>
                    <div className={styles.contentDescription}>
                      <header className={styles.contentHeader}>
                        <p className="hw-relatedContent-p">{label}</p>
                        <Heading headingLevel={4} styleLevel={3}>
                          {item.title.consumer}
                        </Heading>
                      </header>
                      <p
                        className={
                          'hw-relatedContent-p ' + styles.contentAbstract
                        }
                      >
                        {item.abstract.consumer}
                      </p>
                    </div>
                    <div className={styles.contentMedia}>
                      <div className={styles.contentThumbnail}>
                        <button type="button" className={styles.articleButton}>
                          <img src={item.thumbnail} alt={item.title.consumer} />
                        </button>
                      </div>
                    </div>
                    <div className={styles.fullArticle}>
                      <div className={styles.contentFull}>{component}</div>
                    </div>
                  </div>
                </div>
              </article>
            )
            break

          default:
            console.error(`Unknown related content type: ${item.type}`)
            break
        }
      }
    })

    return <section className={styles.relatedContent}>{thumbnails}</section>
  }
}

RelatedContent.propTypes = {
  item: PropTypes.object,
  mediaServiceUrl: PropTypes.string,
  mediaHash: PropTypes.string,
  mediaToken: PropTypes.string
}

RelatedContent.defaultProps = {
  onVideoStart: function() {}
}

export default RelatedContent



// WEBPACK FOOTER //
// /src/~/@healthwise/enhanced-shared-decision-point/components/RelatedContent/index.js